import React, { createContext, useState, useContext, useEffect } from "react";
import axios from "axios";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState(null);
  const [userType, setUserType] = useState(null); // "admin" or "user"
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  useEffect(() => {
    // Check if token exists in localStorage
    const storedToken = localStorage.getItem("authToken");
    const storedUserType = localStorage.getItem("userType");
    const storedUserInfo = localStorage.getItem("userInfo");
    
    if (storedToken && storedUserType) {
      setToken(storedToken);
      setUserType(storedUserType);
      setIsAuthenticated(true);
      
      if (storedUserInfo) {
        setUserInfo(JSON.parse(storedUserInfo));
      }
      
      // Set default authorization header
      axios.defaults.headers.common["Authorization"] = `Bearer ${storedToken}`;
    }
    setLoading(false);
  }, []);

  // Add axios interceptor for handling 401 responses globally
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => {
        // If response is successful, just return it
        return response;
      },
      (error) => {
        // Check if error is 401 (Unauthorized) and user is currently authenticated
        if (error.response?.status === 401 && isAuthenticated) {
          console.log("Session expired - logging out user");
          
          // Show session expired message
          const sessionExpiredEvent = new CustomEvent('sessionExpired', {
            detail: { message: 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.' }
          });
          window.dispatchEvent(sessionExpiredEvent);
          
          // Auto-logout user
          logout(true); // Pass true to indicate session expiration
          
          // Redirect to login with current URL for after-login redirect
          const currentUrl = window.location.pathname + window.location.search;
          setTimeout(() => {
            window.location.href = `/user-login?redirect=${encodeURIComponent(currentUrl)}`;
          }, 100);
        }
        
        // Always reject the promise so components can still handle errors if needed
        return Promise.reject(error);
      }
    );

    // Cleanup interceptor on unmount
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [isAuthenticated]); // Re-run when authentication status changes

  const login = async (emailOrUsername, password, isAdmin = false) => {
    try {
      const response = await axios.post(`${API}/login`, {
        username: emailOrUsername,
        password,
      });

      const { access_token, user_type, user_info } = response.data;
      
      // Store authentication data
      localStorage.setItem("authToken", access_token);
      localStorage.setItem("userType", user_type);
      
      if (user_info) {
        localStorage.setItem("userInfo", JSON.stringify(user_info));
        setUserInfo(user_info);
      }
      
      setToken(access_token);
      setUserType(user_type);
      setIsAuthenticated(true);
      
      // Set default authorization header
      axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
      
      return true;
    } catch (error) {
      console.error("Login failed:", error);
      return false;
    }
  };

  const register = async (firstName, lastName, email, password) => {
    try {
      // First check if email validation is enabled
      const validationResponse = await axios.get(`${API}/email-validation-status`);
      const isValidationEnabled = validationResponse.data.email_validation_enabled;
      
      // Register the user
      const response = await axios.post(`${API}/register`, {
        first_name: firstName,
        last_name: lastName,
        email,
        password,
      });

      // If email validation is disabled, auto-login the user
      if (!isValidationEnabled) {
        const loginSuccess = await login(email, password);
        return { success: true, needsVerification: false, autoLogin: loginSuccess };
      } else {
        // Email validation is enabled, user needs to verify email
        return { success: true, needsVerification: true, autoLogin: false };
      }
    } catch (error) {
      console.error("Registration failed:", error);
      return { success: false, needsVerification: false, autoLogin: false };
    }
  };

  const logout = (isSessionExpired = false) => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("userType");
    localStorage.removeItem("userInfo");
    setToken(null);
    setUserType(null);
    setUserInfo(null);
    setIsAuthenticated(false);
    delete axios.defaults.headers.common["Authorization"];
    
    // Log the logout reason for debugging
    if (isSessionExpired) {
      console.log("User logged out due to session expiration");
    } else {
      console.log("User logged out manually");
    }
  };

  const value = {
    isAuthenticated,
    token,
    userType,
    userInfo,
    login,
    register,
    logout,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};